'use client'

import cn from 'clsx'
import gsap from 'gsap'
import { useLenis } from 'lenis/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Glyphs } from '~/components/button'
import { Link } from '~/components/link'
import { ThemeButton } from '~/components/theme-button'
import { useCartContext } from '~/integrations/shopify/cart/cart-context'
import { useCartModal } from '~/integrations/shopify/cart/modal'
import { useStore } from '~/libs/store'
import s from './mobile-menu.module.css'

const themes = [
  {
    theme: 'light',
  },
  {
    theme: 'simple',
  },
  {
    theme: 'dark',
  },
  {
    theme: 'red',
  },
]

export function MobileMenu({ className, navItems }) {
  const { isNavOpened, setIsNavOpened } = useStore((state) => state)
  const { totalQuantity } = useCartContext()
  const { openCart } = useCartModal()
  const itemCount = totalQuantity()
  const [mounted, setMounted] = useState(false)
  const navRef = useRef(null)
  const themesRef = useRef(null)
  const lenis = useLenis()
  const themeLineRef = useRef(null)

  // Calculate total animation duration
  const navDuration = 0.5
  const navStagger = 0.3
  const themeDuration = 0.5
  const themeStagger = 0.3

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  function handleNavToggle(open) {
    if (open) {
      lenis?.stop()
    } else {
      lenis?.start()
    }
    setIsNavOpened(open)
  }

  const animateNavElements = useCallback((elements) => {
    gsap.fromTo(
      elements,
      {
        opacity: 0,
        y: 60,
      },
      {
        opacity: 1,
        y: 0,
        duration: navDuration,
        stagger: {
          amount: navStagger,
          from: 'start',
        },
        ease: 'power2.out',
      }
    )
  }, [])

  const animateThemeButtons = useCallback((buttons) => {
    gsap.fromTo(
      buttons,
      {
        opacity: 0,
        y: 60,
      },
      {
        opacity: 1,
        y: 0,
        duration: themeDuration,
        delay: 0.25,
        stagger: {
          amount: themeStagger,
          from: 'start',
        },
        ease: 'power2.out',
      }
    )
  }, [])

  const animateOverlay = useCallback((overlay, isOpen) => {
    gsap.to(overlay, {
      opacity: isOpen ? 0.7 : 0,
      ease: 'power2.inOut',
    })
  }, [])

  const animateThemeLine = useCallback(() => {
    gsap.fromTo(
      themeLineRef.current,
      {
        clipPath: 'polygon(0 0, 0 0, 0 100%, 0% 100%)',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        duration: themeDuration,
        delay: 0.45,
        ease: 'power2.inOut',
      }
    )
  }, [])

  useEffect(() => {
    if (!navRef.current || !themesRef.current) return

    const navLinks = navRef.current.querySelectorAll('a')
    const cartButton = document.querySelector(`.${s.cart}`)
    const themeButtons = themesRef.current.querySelectorAll('button')
    const overlay = document.querySelector(`.${s.overlay}`)

    const navElements = [...navLinks]
    if (cartButton) navElements.push(cartButton)

    if (isNavOpened) {
      animateNavElements(navElements)
      animateThemeButtons(themeButtons)
      animateThemeLine()
    }

    animateOverlay(overlay, isNavOpened)
  }, [
    isNavOpened,
    animateNavElements,
    animateThemeButtons,
    animateOverlay,
    animateThemeLine,
  ])

  return (
    <div className={cn(className, 'h-full')}>
      <button
        type="button"
        className={s.hamburger}
        onClick={() => {
          handleNavToggle(!isNavOpened)
        }}
        data-open={isNavOpened}
      >
        <span />
        <span />
        <span />
      </button>

      <div
        className={s.menu}
        data-open={isNavOpened}
      >
        <div className={s.nav}>
          <nav
            ref={navRef}
            className={cn(s.inner, 'grid uppercase spt-8 spb-8')}
          >
            {navItems
              ?.sort((a, b) => a.order - b.order)
              .map(({ label, href }, idx) => (
                <Link
                  key={label}
                  onClick={() => {
                    handleNavToggle(false)
                  }}
                  href={href}
                  className={cn('link bg-contrast p-safe', s.navItem)}
                >
                  {label}
                </Link>
              ))}
          </nav>
          {itemCount > 0 && (
            <button
              type="button"
              className={cn(
                s.cart,
                'link bg-secondary text-primary p-safe uppercase w-full smb-8 flex  items-center'
              )}
              onClick={() => {
                openCart()
                handleNavToggle(false)
              }}
            >
              <Glyphs className={cn(s.glyphs, 'flex items-center')} />
              <span className="sml-8 w-full">
                Cart<sup>[{itemCount.toString().padStart(2, '0')}]</sup>
              </span>
              <Glyphs
                className={cn(s.glyphs, 'flex items-center')}
                isReversed={true}
              />
            </button>
          )}
          <div
            ref={themesRef}
            className={cn(s['theme-container'], 'grid grid-cols-4 sgap-x-4')}
            data-open={isNavOpened}
          >
            <div
              className={s['theme-line']}
              ref={themeLineRef}
            />

            {themes.map(({ theme }) => (
              <ThemeButton
                key={theme}
                className="sh-28 spy-9 spx-8 bg-contrast"
                theme={theme}
              />
            ))}
          </div>
        </div>
      </div>
      <button
        type="button"
        className={cn(s.overlay, !isNavOpened && 'pointer-events-none')}
        onClick={() => {
          handleNavToggle(!isNavOpened)
        }}
      />
    </div>
  )
}
